import ResponsiveContainer from "../DensityResponsiveContainer";
/* eslint-disable */
import { ChartArea, ChartLine, Info, RotateCcw, X as CloseIcon } from "lucide-react";
import { useMemo, useState } from "react";
import { Area, ComposedChart, Line, ReferenceDot, CartesianGrid, Tooltip, XAxis, YAxis } from "recharts";
import VirtualizedDropdown from "../VirtualizedDropdown/VirtualizedDropdown";
import { XBOX_MODES, getHistorySeries, useXboxTitleHistories } from "../../services/xboxDataService";
import { ESTIMATED_PLAYERS_DISCLOSURE, fmtEstimated, fmtInt, fmtShare, fmtSnapshot } from "./xboxUi";

// Same series palette as the Twitch history chart.
export const XBOX_CHART_COLORS = ["#107c10", "#3fa9f5", "#F5E6C8", "#ff9b70", "#33c3eb", "#f1726a", "#66CCFF", "#A855F7", "#FF5342"];
export const XBOX_CHART_MAX = 8;

const MODE_UNIT = { [XBOX_MODES.GAMES]: "game players", [XBOX_MODES.APPS]: "app users", [XBOX_MODES.ALL]: "active users" };
const MODE_BASE = {
  [XBOX_MODES.GAMES]: "share_games (game activity only)",
  [XBOX_MODES.APPS]: "share_apps (app activity only)",
  [XBOX_MODES.ALL]: "share_all (all foreground activity)",
};

function HistoryTooltip({ active, payload, label, titles, mode, metric }) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload || {};
  const items = payload
    .filter((p) => p.value != null)
    .sort((a, b) => b.value - a.value);
  return (
    <div className="xbox-tooltip">
      <div className="xbox-tooltip-head">{fmtSnapshot(label)}</div>
      {items.length === 0 && <div className="xbox-muted">No selected titles observed in this snapshot.</div>}
      {items.map((p) => (
        <div key={p.dataKey} className="xbox-tooltip-row">
          {(() => {
            const titleId = metric === "estimated" ? String(p.dataKey).replace(/__estimated$/, "") : p.dataKey;
            return <>
          <span className="xbox-dot" style={{ background: p.color }} />
          <span className="xbox-tooltip-name">{titles.get(titleId)?.name || titleId}</span>
          <b>{metric === "estimated" ? fmtEstimated(p.value, true) : fmtShare(p.value)}{metric === "estimated" ? " est." : ""}</b>
          <span className="xbox-muted">{row[`${titleId}__rank`] == null ? "—" : `#${row[`${titleId}__rank`]}`}</span>
            </>;
          })()}
        </div>
      ))}
      <div className="xbox-tooltip-foot">
        {fmtInt(row.total)} observed {MODE_UNIT[mode] || "active users"} in snapshot
      </div>
    </div>
  );
}

export default function XboxHistoryChart({ dataset, mode, rows, selectedIds, onAdd, onRemove, onReset }) {
  const history = useXboxTitleHistories(dataset, selectedIds);
  const [metric, setMetric] = useState("estimated");
  const [chartType, setChartType] = useState("line");
  const [timeWindow, setTimeWindow] = useState(null);
  const [highlightedIds, setHighlightedIds] = useState([]);
  const activeHighlights = highlightedIds.filter((id) => selectedIds.includes(id));
  const toggleHighlight = (id, event) => {
    setHighlightedIds((previous) => {
      const current = previous.filter((value) => selectedIds.includes(value));
      if (event.ctrlKey || event.metaKey) return current.includes(id) ? current.filter((value) => value !== id) : [...current, id];
      return current.length === 1 && current[0] === id ? [] : [id];
    });
  };
  const series = useMemo(
    () => (dataset ? getHistorySeries(dataset, mode, selectedIds) : []),
    [dataset, mode, selectedIds, history.version]
  );

  const pickerOptions = useMemo(
    () =>
      rows
        .filter((r) => !selectedIds.includes(r.titleId))
        .map((r) => ({ value: r.titleId, label: `#${r.rank}  ${r.title.name}` })),
    [rows, selectedIds]
  );

  const snaps = dataset?.snapshots || [];
  const periods = snaps.map((snapshot) => snapshot.time);
  const windowKey = `${mode}:${periods[0]}:${periods[periods.length - 1]}:${periods.length}`;
  const zoomRange = timeWindow?.key === windowKey
    ? { start: timeWindow.start, end: timeWindow.end }
    : { start: 0, end: Math.max(0, periods.length - 1) };
  const visibleSeries = series.filter((row) => row.time >= periods[zoomRange.start] && row.time <= periods[zoomRange.end]);
  const visiblePeriods = periods.slice(zoomRange.start, zoomRange.end + 1);
  const tickCount = Math.min(5, visiblePeriods.length);
  const ticks = Array.from({ length: tickCount }, (_, index) =>
    visiblePeriods[tickCount === 1 ? 0 : Math.round(index * (visiblePeriods.length - 1) / (tickCount - 1))]
  );
  const first = snaps[zoomRange.start];
  const last = snaps[zoomRange.end];
  const peaks = selectedIds.map((id) => {
    const dataKey = metric === "estimated" ? `${id}__estimated` : id;
    return visibleSeries.reduce((peak, row) =>
      Number.isFinite(row.time) && Number.isFinite(row[dataKey]) && (!peak || row[dataKey] > peak[dataKey]) ? row : peak,
    null);
  });

  return (
    <div className="xbox-panel xbox-history">
      <div className="xbox-panel-head">
        <div>
          <div className="xbox-panel-title">{metric === "estimated" ? "Estimated Players over time" : "Player Share over time"}</div>
          <div className="xbox-panel-sub">
            {snaps.length} snapshot{snaps.length === 1 ? "" : "s"}
            {first && last && snaps.length > 1 ? ` · ${fmtSnapshot(first.time)} → ${fmtSnapshot(last.time)}` : ""}
            {" · "}{metric === "estimated" ? "per-snapshot time-of-day calibration; Games use share_games, Apps / All use share_all" : MODE_BASE[mode]}
          </div>
        </div>
        <div className="xbox-history-tools">
          <div className="xbox-metric-toggle xbox-history-chart-toggle" role="group" aria-label="Chart style">
            <button type="button" aria-label="Line chart" title="Line chart" aria-pressed={chartType === "line"} className={chartType === "line" ? "is-active" : ""} onClick={() => setChartType("line")}><ChartLine size={16} /></button>
            <button type="button" aria-label="Area chart" title="Area chart" aria-pressed={chartType === "area"} className={chartType === "area" ? "is-active" : ""} onClick={() => setChartType("area")}><ChartArea size={16} /></button>
          </div>
          <div className="xbox-metric-toggle" role="tablist" aria-label="History metric">
            <button type="button" role="tab" aria-selected={metric === "share"} className={metric === "share" ? "is-active" : ""} onClick={() => setMetric("share")}>Player Share</button>
            <button type="button" role="tab" aria-selected={metric === "estimated"} className={metric === "estimated" ? "is-active" : ""} onClick={() => setMetric("estimated")} title={ESTIMATED_PLAYERS_DISCLOSURE}>Est. Players <Info size={12} /></button>
          </div>
          <div className="xbox-history-picker">
            <VirtualizedDropdown
              placeholder={selectedIds.length >= XBOX_CHART_MAX ? `Max ${XBOX_CHART_MAX} titles` : "Add a title…"}
              options={pickerOptions}
              isMulti={false}
              isClearable={false}
              isDisabled={selectedIds.length >= XBOX_CHART_MAX}
              value={null}
              maxMenuWidth={360}
              menuWidth={360}
              onChange={(opt) => opt && onAdd(opt.value)}
            />
          </div>
          <button type="button" className="xbox-pill-btn xbox-history-clear" title="Clear chart selection" aria-label="Clear chart selection" onClick={onReset}>
            <RotateCcw size={16} />
          </button>
        </div>
      </div>

      {selectedIds.length > 0 && history.loading && <div role="status" className="xbox-panel-sub">Loading selected title history…</div>}
      {selectedIds.length > 0 && history.error && <div role="alert" className="xbox-panel-sub">
        {history.error} <button type="button" className="xbox-pill-btn" onClick={history.retry}>Retry history</button>
      </div>}
      <div className="xbox-history-body">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={visibleSeries} margin={{ top: 40, right: 60, left: 0, bottom: 6 }}>
            <CartesianGrid stroke="#ffffff" strokeOpacity={0.12} strokeDasharray="2 4" vertical={false} />
            <XAxis
              dataKey="time"
              type="number"
              scale="time"
              domain={["dataMin", "dataMax"]}
              ticks={ticks}
              minTickGap={48}
              interval="preserveStartEnd"
              tickLine={false}
              axisLine={false}
              stroke="#ddd"
              tick={{ fontSize: 11, fontWeight: 700, fill: "#cfd8e3" }}
              tickFormatter={(time) => new Date(time).toLocaleDateString("en-US", { month: "numeric", day: "2-digit", year: "2-digit" })}
              padding={{ left: 0, right: 0 }}
            />
            <YAxis
              stroke="#ddd"
              width={100}
              tickCount={5}
              tickLine={false}
              axisLine={false}
              tick={{ fontSize: 12, fontWeight: 400, fill: "#cfd8e3" }}
              tickFormatter={(v) => metric === "estimated" ? fmtEstimated(v) : `${Number(v).toFixed(v >= 10 ? 0 : 1)}%`}
            />
            <Tooltip content={<HistoryTooltip titles={dataset?.titles || new Map()} mode={mode} metric={metric} />} />
            {selectedIds.map((id, i) => {
              const color = XBOX_CHART_COLORS[i % XBOX_CHART_COLORS.length];
              const dataKey = metric === "estimated" ? `${id}__estimated` : id;
              const SeriesComponent = chartType === "area" ? Area : Line;
              const focused = !activeHighlights.length || activeHighlights.includes(id);
              return (
                <SeriesComponent
                  key={`${id}:${chartType}`}
                  type="monotone"
                  dataKey={dataKey}
                  name={dataset?.titles.get(id)?.name || id}
                  stroke={color}
                  fill={color}
                  fillOpacity={focused ? 0.07 : 0.015}
                  strokeOpacity={focused ? 1 : 0.35}
                  strokeWidth={focused ? 2.4 : 1.2}
                  dot={{ r: 3.5, fill: color, stroke: "#000", strokeWidth: 1, opacity: focused ? 1 : 0.2 }}
                  activeDot={{ r: 5 }}
                  connectNulls={false}
                  isAnimationActive={true}
                  animationDuration={700}
                />
              );
            })}
            {selectedIds.map((id, index) => {
              const peak = peaks[index];
              if (!peak) return null;
              const dataKey = metric === "estimated" ? `${id}__estimated` : id;
              const color = XBOX_CHART_COLORS[index % XBOX_CHART_COLORS.length];
              if (activeHighlights.length && !activeHighlights.includes(id)) return null;
              const name = dataset?.titles.get(id)?.name || id;
              return <ReferenceDot key={`peak:${id}`} x={peak.time} y={peak[dataKey]} r={5} fill={color} stroke="rgba(0,0,0,0.85)" strokeWidth={2} isFront label={(props) => {
                const pointX = Number(props.x ?? props.viewBox?.x ?? 0);
                const pointY = Number(props.y ?? props.viewBox?.y ?? 0);
                const nearRightEdge = peak.time >= visiblePeriods[Math.max(0, visiblePeriods.length - 3)];
                return <text className="xbox-history-peak-label" x={nearRightEdge ? pointX - 8 : pointX + 8} y={Math.max(14, pointY - 6)} textAnchor={nearRightEdge ? "end" : "start"} fill={color} fontSize={12} fontWeight={700} style={{ cursor: "pointer", pointerEvents: "auto", userSelect: "none", paintOrder: "stroke", stroke: "rgba(0,0,0,0.75)", strokeWidth: 3 }} onClick={(event) => { event.stopPropagation(); toggleHighlight(id, event); }}>{name}</text>;
              }} />;
            })}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      {periods.length > 1 && (
        <div className="xbox-history-timeline">
          <div className="xbox-range-rail" />
          <div className="xbox-range-selection" style={{ left: `${100 * zoomRange.start / (periods.length - 1)}%`, width: `${100 * (zoomRange.end - zoomRange.start) / (periods.length - 1)}%` }} />
          <input className="xbox-history-range" type="range" aria-label="Chart period start" min={0} max={periods.length - 1} value={zoomRange.start} onChange={(event) => setTimeWindow({ key: windowKey, start: Math.min(Number(event.target.value), zoomRange.end - 1), end: zoomRange.end })} />
          <input className="xbox-history-range" type="range" aria-label="Chart period end" min={0} max={periods.length - 1} value={zoomRange.end} onChange={(event) => setTimeWindow({ key: windowKey, start: zoomRange.start, end: Math.max(Number(event.target.value), zoomRange.start + 1) })} />
        </div>
      )}
      <div className="xbox-history-footer">
        <div className="xbox-history-chips" role="list" aria-label="Selected chart titles">
          {selectedIds.map((id, index) => {
            const name = dataset?.titles.get(id)?.name || id;
            return (
              <span key={id} className="xbox-series-chip" role="listitem">
                <span className="xbox-dot" style={{ background: XBOX_CHART_COLORS[index % XBOX_CHART_COLORS.length] }} />
                <button type="button" className="xbox-series-name" aria-label={`Highlight ${name}`} aria-pressed={activeHighlights.includes(id)} title={`Highlight ${name}`} onClick={(event) => toggleHighlight(id, event)}>{name}</button>
                <button type="button" title={`Remove ${name}`} aria-label={`Remove ${name}`} onClick={() => onRemove(id)}><CloseIcon size={12} /></button>
              </span>
            );
          })}
        </div>
      </div>
    </div>
  );
}
